/**
 * DecisionCard Component
 * Mobile-friendly card layout for Decision Board issues
 * Displays all issue data in a vertical, touch-optimized format
 */

import React from 'react';
import './DecisionCard.css';

interface Posture {
  score: number;
  category: string;
  color: string;
  article_component: number;
  stakeholder_component: number;
}

interface Velocity {
  fv_score: number;
  recent_articles_24h: number;
  trend: string;
}

interface Spillover {
  percentage: number;
  index: number;
  risk_level: string;
  color: string;
  shared_stakeholders: number;
  total_stakeholders: number;
}

interface Issue {
  id: string;
  title: string;
  description: string;
  overall_score: number;
  decision_mode: string;
  posture: Posture;
  velocity: Velocity;
  spillover: Spillover;
  stakeholder_count: number;
  article_count: number;
  priority_level: string;
  status: string;
}

interface DecisionCardProps {
  issue: Issue;
  getDecisionModeColor: (mode: string) => string;
}

const DecisionCard: React.FC<DecisionCardProps> = ({ issue, getDecisionModeColor }) => {
  return (
    <div className="decision-card">
      {/* Header with Issue Title and Mode Badge */}
      <div className="decision-card-header">
        <h3 className="decision-card-title">{issue.title}</h3>
        <span 
          className="decision-card-mode-badge"
          style={{ backgroundColor: getDecisionModeColor(issue.decision_mode) }}
        >
          {issue.decision_mode}
        </span>
      </div>

      {/* HEAT Score - Prominent Display */}
      <div className="decision-card-heat">
        <span className="decision-card-heat-label">HEAT Score</span>
        <span className="decision-card-heat-value">{issue.overall_score?.toFixed(1) || '0.0'}</span>
      </div>

      {/* Metrics Grid */}
      <div className="decision-card-metrics">
        {/* Posture */}
        <div className="decision-card-metric">
          <span className="decision-card-metric-label">Posture</span>
          <div className="decision-card-metric-value">
            <span 
              className="posture-score"
              style={{ color: issue.posture?.color || '#6B7280' }}
            >
              {issue.posture?.score?.toFixed(2) || '0.00'}
            </span>
            <span className="posture-category">{issue.posture?.category || 'N/A'}</span>
          </div>
        </div>

        {/* Velocity */}
        <div className="decision-card-metric">
          <span className="decision-card-metric-label">Velocity</span>
          <div className="decision-card-metric-value">
            <span className="velocity-score">{issue.velocity?.fv_score?.toFixed(1) || '0.0'}</span>
            <span className="velocity-trend">{issue.velocity?.trend || 'stable'}</span>
          </div>
        </div>

        {/* Spillover */}
        <div className="decision-card-metric">
          <span className="decision-card-metric-label">Spillover</span>
          <div className="decision-card-metric-value">
            <span 
              className="spillover-percentage"
              style={{ color: issue.spillover?.color || '#6B7280' }}
            >
              {issue.spillover?.percentage?.toFixed(1) || issue.spillover?.index?.toFixed(1) || '0.0'}%
            </span>
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="decision-card-counts">
        <div className="decision-card-count">
          <span className="count-value">{issue.stakeholder_count}</span>
          <span className="count-label">Stakeholders</span>
        </div>
        <div className="decision-card-count">
          <span className="count-value">{issue.article_count}</span>
          <span className="count-label">Articles</span>
        </div>
      </div>
    </div>
  );
};

export default DecisionCard;
